import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import {
  loadList,
  loadNextListProducts,
  pushProductRefsToListAsOptimisticUI,
} from '@epicuro-next/state/list-entities';
import { upsertProducts } from '@epicuro-next/state/product-entities';
import { setActiveList } from '@epicuro-next/state/product-list-state';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { selectUserName } from '@epicuro-next/state/user-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import {
  addToList,
  addToListFail,
  addToListSuccess,
  barcodeRead,
  targetChange,
} from '../barcode-reader';
import { ListTarget, ScannerStatus } from '../models';
import { ScannerApiService } from '../service/scanner-api.service';

@Injectable()
export class ScanToListEffects {
  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
  );
  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  constructor(
    private actions$: Actions,
    private store: Store,
    private scannerApi: ScannerApiService,
    private productListApi: ProductListApiService,
  ) {}

  /**
   * Loads list and relevant products when scan target changed to a list
   */
  public setCurrentListOnTargetChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(targetChange),
      map(({ target }) => {
        if (target?.type === 'List') return target.list.id;
      }),
      filter<string>(Boolean),
      switchMap((id) => [
        setActiveList({ id }),
        loadList({ listId: id }),
        loadNextListProducts({ listId: id }),
      ]),
    ),
  );

  public addToListHandleUndefinedLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addToList),
      filter((action) => isNullish(action.target.list.id)),
      switchMap((action) =>
        this.createList().pipe(
          map(({ listId, name }) => ({
            newTarget: {
              type: 'List',
              list: { id: listId, name },
            } as ListTarget,
            addToListAction: action,
            errorAction: null,
          })),
          catchError(() =>
            of({
              newTarget: null,
              addToListAction: null,
              errorAction: addToListFail(action),
            }),
          ),
        ),
      ),
      switchMap((value) => {
        if (value.errorAction) {
          return [value.errorAction];
        } else if (value.newTarget) {
          return [
            targetChange({ target: value.newTarget }),
            addToList({
              ...value.addToListAction,
              target: value.newTarget,
            }),
          ];
        } else {
          // We are getting this error after updating to Angular 12, this error will never happen
          return [];
        }
      }),
    ),
  );

  /**
   * Verifies scanned product and continues to add to list request.
   */
  public addToListIfListDefined$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addToList),
      filter((action) => isNonNullish(action.target.list.id)),
      switchMap((action) => {
        const productsToParent = [
          action.product,
          action.product.parent,
          action.product.parent?.parent,
        ].filter(isNonNullish);
        const firstMarketableProduct = productsToParent.find(
          (p) => p.canBeMarketed,
        );
        if (!firstMarketableProduct) {
          return of(
            addToListFail({
              ...action,
              reason: ScannerStatus.ERROR_ADD_LIST_NOT_MARKETABLE,
            }),
          );
        }
        // restart the flow so caching and error handling can work for duped scans of variants
        if (firstMarketableProduct.productId !== action.barcode) {
          return of(barcodeRead({ barcode: firstMarketableProduct.productId }));
        }
        return this.scannerApi
          .addProductToList(action.target.list?.id ?? '', action.product)
          .pipe(
            map(() => addToListSuccess(action)),
            catchError(() => of(addToListFail(action))),
          );
      }),
    ),
  );

  /**
   * When add to list is successfull, updates list entities state
   */
  public updateListEntitiesOnScanProductToList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addToListSuccess),
      filter((a) => isNonNullish(a.target.list)),
      switchMap((action) => {
        const products = [action.product].filter(isNonNullish) as Product[];
        const listId = action.target.list.id as string;

        return [
          upsertProducts({ products }),
          pushProductRefsToListAsOptimisticUI({
            listId,
            productIds: products.map((product) => product.productId),
            addToTop: true,
          }),
        ];
      }),
    ),
  );

  private createList(): Observable<{ name: string; listId: string }> {
    return combineLatest([
      this.store.pipe(select(selectUserName)),
      this.accountId$,
      this.siteId$,
    ]).pipe(
      take(1),
      switchMap(([userName, accountId, siteId]) => {
        const name = this.listName(
          userName?.firstName ?? userName?.displayName,
        );
        return this.productListApi
          .create({
            name,
            accountId,
            siteId,
          })
          .pipe(map((list) => ({ name, listId: list?.id })));
      }),
    );
  }

  private listName(userName?: string) {
    if (!userName) {
      return $localize`:scanner|Scanner new list name with random id@@scannerNewListAutoGeneratedNameWithoutUserName:Scan list, ${this.randomId()}`;
    }
    return $localize`:scanner|Scanner new list name with user name and random id@@scannerNewListAutoGeneratedNameWithUserName:${userName}'s scan list, ${this.randomId()}`;
  }

  private randomId() {
    return 10000 + Math.floor(90000 * Math.random());
  }
}
